<template>
  <div class="d-flex h-100" height="100%">
    <v-card class="d-flex flex-column h-100 flex-grow-1 justify-start" elevation="0" outlined v-if="!loading && game" min-height="350" height="100%">
      <v-card-title class="py-1 px-4 white--text d-flex align-center justify-space-between" style="flex-wrap: nowrap;" :class="$vuetify.theme.dark ? 'black' : 'primary'" >
        <div class="d-flex align-center" style="flex: 1 1 auto; min-width: 0;">
          <v-icon color="white" medium class="mr-2">track_changes</v-icon>
          <span class="white--text body-font body-1 font-weight-bold text-uppercase text-ellipsis">{{$t("dashboards.nextGame")}}</span>
        </div>
        <v-btn v-if="rivalId" class="pl-0 pr-0" text small :to="`/team/${rivalId}/analysis`" style="flex-shrink: 0; min-width: auto;">
          <span class="white--text text-decoration-underline font-weight-bold mr-1">{{$t('general.seeAll')}}</span>
          <v-icon small color="white">keyboard_arrow_right</v-icon>
        </v-btn>
      </v-card-title>
      <!-- League + Date Section -->
      <div class="d-flex align-center justify-center px-4 pt-3 pb-2" v-if="game">
        <img
          v-if="game.leagueId && !game.leagueImgError"
          :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/' + game.leagueId + '.png'"
          height="18"
          contain
          class="mr-2"
          @error="onImageLoadFailure($event, 'league')"
        />
        <span class="body-2 primary--text font-weight-bold mr-2">{{game.leagueName}}</span>
        <span v-if="game.leagueId" class="mr-2 primary--text">-</span>
        <span class="caption primary--text">{{ getLocalMatchDate(game.matchDate, "DD/MM/YYYY") }}</span>
      </div>

      <!-- Match Section with Larger Crests -->
      <div class="d-flex align-center justify-center px-4 py-3">
        <!-- Home Team Crest -->
        <div class="d-flex flex-column align-center mr-7">
          <v-tooltip top color="lightSecondary lighten-4" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="display: inline-block;">
                <router-link
                  :to="`/team/${game.homeTeamId}`"
                  class="d-flex align-center justify-center"
                  style="text-decoration: none;"
                >
                  <v-img
                    :src="game.homeTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.homeTeamId + '.png'"
                    :height="$vuetify.breakpoint.smAndDown ? '35' : '45'"
                    :width="$vuetify.breakpoint.smAndDown ? '35' : '45'"
                    contain
                    v-on:error="onImgError($event,'home')"
                  />
                </router-link>
              </div>
            </template>
            <span class="black--text body-1">{{game.homeTeamName}}</span>
          </v-tooltip>
        </div>

        <!-- VS (no score for future games) -->
        <div class="d-flex align-center text-h4 font-weight-bold primary--text" style="min-width: 80px; justify-content: center;">
          <span class="mx-2">VS</span>
        </div>

        <!-- Away Team Crest -->
        <div class="d-flex flex-column align-center ml-4">
          <v-tooltip top color="lightSecondary lighten-4" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="display: inline-block;">
                <router-link
                  :to="`/team/${game.awayTeamId}`"
                  class="d-flex align-center justify-center"
                  style="text-decoration: none;"
                >
                  <v-img
                    :src="game.awayTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.awayTeamId + '.png'"
                    :height="$vuetify.breakpoint.smAndDown ? '35' : '45'"
                    :width="$vuetify.breakpoint.smAndDown ? '35' : '45'"
                    contain
                    v-on:error="onImgError($event,'away')"
                  />
                </router-link>
              </div>
            </template>
            <span class="black--text body-1">{{game.awayTeamName}}</span>
          </v-tooltip>
        </div>
      </div>
      <v-divider class="my-1"></v-divider>
      <div v-if="homeStats && awayStats">
        <!-- 1. Average Expected Goals (xG) -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('xG', homeStats.xGPerGame, awayStats.xGPerGame, 'home')"
            >{{formatNumberInput(homeStats.xGPerGame)}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.averagexG')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('xG', homeStats.xGPerGame, awayStats.xGPerGame, 'away')"
            >{{formatNumberInput(awayStats.xGPerGame)}}</span>
          </div>
        </div>
        <v-divider class="my-1"></v-divider>
        
        <!-- 2. Average Possession -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('possession', homeStats.possession, awayStats.possession, 'home')"
            >{{homeStats.possession ? formatNumberInput(homeStats.possession) + '%' : '-'}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.averagePossession')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('possession', homeStats.possession, awayStats.possession, 'away')"
            >{{awayStats.possession ? formatNumberInput(awayStats.possession) + '%' : '-'}}</span>
          </div>
        </div>
        <v-divider class="my-1"></v-divider>
        
        <!-- 3. Average Shots -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('shots', homeStats.shotsPerGame, awayStats.shotsPerGame, 'home')"
            >{{formatNumberInput(homeStats.shotsPerGame)}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.averageShots')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('shots', homeStats.shotsPerGame, awayStats.shotsPerGame, 'away')"
            >{{formatNumberInput(awayStats.shotsPerGame)}}</span>
          </div>
        </div>
        <v-divider class="my-1"></v-divider>
        
        <!-- 4. Average Shots on Target -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('shotsOnTarget', homeStats.shotsOnTargetPerGame, awayStats.shotsOnTargetPerGame, 'home')"
            >{{formatNumberInput(homeStats.shotsOnTargetPerGame)}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.shotsOnTarget')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('shotsOnTarget', homeStats.shotsOnTargetPerGame, awayStats.shotsOnTargetPerGame, 'away')"
            >{{formatNumberInput(awayStats.shotsOnTargetPerGame)}}</span>
          </div>
        </div>
        <v-divider class="my-1"></v-divider>
        
        <!-- 5. Average PPDA -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('ppda', homeStats.ppdaPerGame, awayStats.ppdaPerGame, 'home')"
            >{{formatNumberInput(homeStats.ppdaPerGame)}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.ppda')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('ppda', homeStats.ppdaPerGame, awayStats.ppdaPerGame, 'away')"
            >{{formatNumberInput(awayStats.ppdaPerGame)}}</span>
          </div>
        </div>
      </div>
    </v-card>
    <v-card elevation="0" outlined class="d-flex justify-center align-center py-8 px-12" v-if="loading">
      <v-progress-circular :size="75" color="secondary" indeterminate></v-progress-circular>
    </v-card>
    <v-card elevation="0" outlined v-if="!loading && !game" class="d-flex flex-column flex-grow-1">
      <v-card-title :class="$vuetify.theme.dark ? 'black' : 'primary'" class="py-2 white--text d-flex align-center">
        <v-icon color="white" medium class="mr-2">track_changes</v-icon>
        <span class="white--text body-font body-1 font-weight-bold text-uppercase">{{$t("dashboards.nextGame")}}</span>
      </v-card-title>
      <div class="d-flex justify-center align-center flex-column px-3 py-4 mb-13 text-center flex-grow-1">
        <v-icon color="primary lighten-5" v-bind:style="{'font-size':$vuetify.breakpoint.mdAndUp ? '80px' : '60px'}">event_busy</v-icon>
        <span class="primary--text lighten--text-2 body-1">{{$t("dashboards.placeholderNextGame")}}</span>
      </div>
    </v-card>
  </div>
</template>

<script>

import GameService from "../../../services/game/game.service";
import TeamService from "../../../services/team/team.service";
import ResizeObserver from 'resize-observer-polyfill';
import { userHasRole, getNeutralColors, getLocalMatchDate } from "../../../utils/functions";

export default {
  name: "NextGameWidget",
  components: {
  },
  props: {
    teamId: {
      type: Number,
      required: true
    },
    seasonId: {
      type: Number,
      required: false
    },
  },
  data() {
    return {
      team: null,
      game: null,
      rivalId: null,
      homeStats: null,
      awayStats: null,
      loading: false,
      observer: null
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    homeTeamColor() {
      if (this.game && this.game.homeTeamColor) {
        return this.game.homeTeamColor.replace("0x", "#");
      }
      return getNeutralColors('home')[0];
    },
    awayTeamColor() {
      if (this.game && this.game.awayTeamColor) {
        return this.game.awayTeamColor.replace("0x", "#");
      }
      return getNeutralColors('away')[0];
    }
  },
  filters: {
  },
  mounted() {
    this.loadViewData();
    this.observer = new ResizeObserver(this.onResize)
    .observe(this.$el)
  },
  destroyed() {
    delete this.observer;
  },
  methods: {
    onResize () {
      this.$emit('heightChange', this.$el.clientHeight)
    },
    async loadViewData() {
      await this.loadGame();
      if(this.game){
        this.homeStats = await this.loadTeamAvgStats(this.game.homeTeamId, 1);
        this.awayStats = await this.loadTeamAvgStats(this.game.awayTeamId, 2);
      }
    },
    async loadTeam(){
      this.loading = true;
      let teamResponse = await TeamService.getTeamInfo(this.teamId);
      this.team = teamResponse.data;
      this.loading = false;
    },
    async loadGame() {
      this.loading = true;
      let gameResponse = await GameService.getNextGames(this.teamId, 1);
      this.game = gameResponse.data[0];
      if (this.game)
        this.rivalId = this.teamId === this.game.homeTeamId ? this.game.awayTeamId : this.game.homeTeamId;
      this.loading = false;
    },
    async loadTeamAvgStats(teamId,side) {
      let teamResponse = await TeamService.getTeamAvgStats(
        teamId,
        this.seasonId ? this.seasonId : this.game.seasonId
      );
      let stats = _.find(teamResponse.data,(d) => d.groundId === side);
      if(!stats){
        stats = _.find(teamResponse.data,(d) => d.groundId === 3);
      }
      return stats;
    },
    onImgError(event,side){
      if(side === 'home'){
        this.game.homeTeamImgError = true;
      }else if(side === 'away'){
        this.game.awayTeamImgError = true;
      }
    },
    onImageLoadFailure(event, type) {
      if (type === "league" && event.target) {
        event.target.style.display = "none";
        if (this.game) {
          this.game.leagueImgError = true;
        }
      }
    },
    getLocalMatchDate(date, format) {
      return getLocalMatchDate(date, format);
    },
    getBetterTeam(metric, homeValue, awayValue) {
      // Returns 'home', 'away', or 'tie'
      if (homeValue === null || homeValue === undefined || isNaN(homeValue)) return awayValue !== null && awayValue !== undefined && !isNaN(awayValue) ? 'away' : 'tie';
      if (awayValue === null || awayValue === undefined || isNaN(awayValue)) return homeValue !== null && homeValue !== undefined && !isNaN(homeValue) ? 'home' : 'tie';

      if (metric === 'ppda') { // Lower is better for PPDA
        return homeValue < awayValue ? 'home' : (awayValue < homeValue ? 'away' : 'tie');
      } else { // Higher is better for other metrics
        return homeValue > awayValue ? 'home' : (awayValue > homeValue ? 'away' : 'tie');
      }
    },
    getValueColorClass(metric, homeValue, awayValue, side) {
      const betterTeam = this.getBetterTeam(metric, homeValue, awayValue);
      if (!betterTeam || betterTeam === 'tie') {
        return 'metric-tie';
      }
      if (betterTeam === side) {
        return 'metric-better';
      }
      return 'metric-worse';
    },
    userHasRole(role) {
      return userHasRole(this.currentUser, role);
    },
    formatNumberInput(numValue, precision = 1) {
      if (numValue === null || numValue === undefined || isNaN(numValue)) {
        return '-';
      }
      return parseFloat(numValue.toFixed(precision));
    }
  }
};
</script>

<style scoped>
.metric-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.metric-side {
  flex: 0 0 60px; 
  display: flex;
}

.metric-side:first-child {
  justify-content: flex-start;
}

.metric-side:last-child {
  justify-content: flex-end;
}

.metric-label {
  flex: 1;
  text-align: center;
  padding: 0 8px;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-box-orient: vertical;  
  overflow: hidden;
  line-height: 1.2;
}

.metric-value {
  white-space: nowrap;
}

.metric-value-container {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  text-align: center;
  min-width: 50px;
}

.metric-better {
  background-color: #2e7d32 !important; /* Green background */
  color: #ffffff !important; /* White text */
  font-weight: bold !important;
}

.metric-worse {
  background-color: #c62828 !important; /* Red background */
  color: #ffffff !important; /* White text */
  font-weight: bold !important;
}

.metric-tie {
  background-color: rgba(150, 150, 150, 0.9) !important; /* Gray with a bit of opacity background */
  color: white !important;
  font-weight: bold !important;
}
</style>